import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar, FilterBar, Tabs } from '@opendate-app/setlist';
import ReportingPageLayout from './ReportingPageLayout';
import BookingKpiCards from './booking/BookingKpiCards';
import BookingTrendCard from './booking/BookingTrendCard';
import BookingForwardCalendarCard from './booking/BookingForwardCalendarCard';
import BookingRelationshipsCard from './booking/BookingRelationshipsCard';
import BookingBreakdownCard from './booking/BookingBreakdownCard';
import { buildBookingParams, keepSelectedOptions, useBookingFetch } from './booking/bookingUtils';
import useReportingPage from '../hooks/useReportingPage';

const axios = require('axios').default;

const DATE_MODES = [
  { value: 'created', label: 'Date Created' },
  { value: 'event_date', label: 'Event Date' },
];
const COMPARE_MODES = [
  { value: 'last_year', label: 'Last year' },
  { value: 'prior', label: 'Prior period' },
];

const toolbarLabel = { fontSize: 13, color: 'var(--sl-text-tertiary)', fontWeight: 500, whiteSpace: 'nowrap' };
const toolbarDivider = { width: 1, height: 20, background: 'var(--sl-border-primary)', flexShrink: 0 };

const pad = (n) => String(n).padStart(2, '0');
const toIsoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIsoDate = (iso) => new Date(`${iso}T00:00:00`);
const noop = () => {};

function CreatedDateEditor({ values, onChange, close }) {
  const value = values.length === 2 ? { start: fromIsoDate(values[0]), end: fromIsoDate(values[1]) } : null;
  return (
    <div className="p-2">
      <Calendar
        mode="range"
        value={value}
        onChange={(range) => {
          if (!range?.start || !range?.end) return;
          onChange([toIsoDate(range.start), toIsoDate(range.end)]);
          close();
        }}
      />
    </div>
  );
}

export function buildFilterDefs(venueOwnerships, filterOptions) {
  const options = (list) => (list || []).map((o) => ({ value: String(o.id), label: o.name }));
  return [
    { key: 'venue_ownership_id', label: 'Venue', options: options(venueOwnerships) },
    { key: 'buyer_id', label: 'Talent buyer', type: 'search', options: options(filterOptions?.buyers) },
    { key: 'genre_id', label: 'Genre', options: options(filterOptions?.genres) },
    { key: 'promoter_id', label: 'Promoter', type: 'search', options: options(filterOptions?.promoters) },
    { key: 'team_membership_id', label: 'Created by', type: 'search', options: options(filterOptions?.team_members) },
    { key: 'created_date', label: 'Created date', render: (props) => <CreatedDateEditor {...props} /> },
  ];
}

const formatFilterValue = (filter, values) => {
  if (!values?.length) return 'Any';
  if (filter.key === 'created_date') {
    return values.map((iso) => fromIsoDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })).join(' – ');
  }
  if (values.length === 1) return filter.options.find((o) => o.value === values[0])?.label || '1 selected';
  return `${values.length} selected`;
};

function ReportingBookingPage({ team, venueOwnerships }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    activeFilters, setActiveFilters, comparison,
  } = useReportingPage('year_to_date');

  const [dateMode, setDateMode] = useState('created');
  const [compareTo, setCompareTo] = useState('last_year');
  const [relationshipsBy, setRelationshipsBy] = useState('agent');
  const [breakdownBy, setBreakdownBy] = useState('venue');
  const [filterOptions, setFilterOptions] = useState(null);

  const base = `/teams/${team?.id}/reporting`;

  useEffect(() => {
    if (!team?.id) return undefined;
    let active = true;
    const params = { start_date: dateRange?.start, end_date: dateRange?.end };
    if (dateMode === 'event_date') params.date_mode = 'event_date';
    axios.get(`${base}/booking_filter_options.json`, { params })
      .then((res) => { if (active) setFilterOptions(res.data || null); })
      .catch((err) => console.error(err));
    return () => { active = false; };
  }, [team?.id, dateRange, dateMode]);

  const filterLabels = useRef({});
  const filterDefs = useMemo(
    () => keepSelectedOptions(buildFilterDefs(venueOwnerships, filterOptions), activeFilters, filterLabels.current),
    [venueOwnerships, filterOptions, activeFilters],
  );
  const params = useMemo(
    () => buildBookingParams({ dateRange, comparison, dateMode, activeFilters }),
    [dateRange, comparison, dateMode, activeFilters],
  );
  const forwardParams = useMemo(() => {
    const { start_date, end_date, prior_start_date, prior_end_date, date_mode, ...rest } = params;
    return rest;
  }, [params]);

  const kpis = useBookingFetch(`${base}/booking_kpis.json`, params);
  const trend = useBookingFetch(`${base}/booking_trend.json`, params);
  const forward = useBookingFetch(`${base}/booking_forward_calendar.json`, forwardParams);
  const relationships = useBookingFetch(`${base}/booking_relationships.json`, { ...params, by: relationshipsBy });
  const breakdown = useBookingFetch(`${base}/booking_breakdown.json`, { ...params, by: breakdownBy });

  const toolbarExtras = (
    <>
      <div style={toolbarDivider} />
      <span style={toolbarLabel}>View by:</span>
      <Tabs variant="button-border" size="sm" collapseBelow={0} items={DATE_MODES} value={dateMode} onChange={setDateMode} />
      <div style={toolbarDivider} />
      <span style={toolbarLabel}>Compare to:</span>
      <Tabs variant="button-border" size="sm" collapseBelow={0} items={COMPARE_MODES} value={compareTo} onChange={setCompareTo} />
      <div className="w-100">
        <FilterBar
          variant="plain"
          filters={filterDefs}
          values={activeFilters}
          onChange={setActiveFilters}
          formatValue={formatFilterValue}
          onClearAll={() => setActiveFilters({})}
        />
      </div>
    </>
  );

  return (
    <ReportingPageLayout
      team={team}
      activeKey="booking"
      title="Booking"
      subtitle="How your booking is pacing: volume, conversion, and who you're booking with."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      showFilters={false}
      activeFilters={{}}
      filterGroups={[]}
      filterSidebarOpen={false}
      setFilterSidebarOpen={noop}
      handleFilterChange={noop}
      handleRemoveFilter={noop}
      handleClearAllFilters={noop}
      toolbarExtras={toolbarExtras}
    >
      <BookingKpiCards kpis={kpis.data} loading={kpis.loading} compareTo={compareTo} isMobile={isMobile} />

      <div className="mb-3">
        <BookingTrendCard trend={trend.data} loading={trend.loading} error={trend.error} />
      </div>

      <div className="mb-3">
        <BookingForwardCalendarCard months={forward.data} loading={forward.loading} error={forward.error} />
      </div>

      <div className="mb-3">
        <BookingRelationshipsCard
          data={relationships.data}
          loading={relationships.loading}
          error={relationships.error}
          by={relationshipsBy}
          onByChange={setRelationshipsBy}
          isMobile={isMobile}
        />
      </div>

      <div style={{ paddingBottom: 100 }}>
        <BookingBreakdownCard
          data={breakdown.data}
          loading={breakdown.loading}
          error={breakdown.error}
          by={breakdownBy}
          onByChange={setBreakdownBy}
        />
      </div>
    </ReportingPageLayout>
  );
}

export default ReportingBookingPage;
